import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Info, Lightbulb, Trash2, UserRound, X } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button as MuiButton,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  createNutritionistPlan,
  createNutritionistProgramme,
  deleteNutritionistPlan,
  deleteNutritionistProgramme,
  getNutritionistPlans,
  updateNutritionistPlan,
  updateNutritionistProgramme,
  type CreateNutritionistProgrammePayload,
  type NutritionistPlan,
  type NutritionistProgramme,
  type ProgrammeDeliveryMode,
  type ProgrammePricingType,
} from '@/lib/api/plans';
import {
  createNutritionistConsultationType,
  deleteNutritionistConsultationType,
  getNutritionistConsultationTypes,
  updateNutritionistConsultationType,
  type ConsultationMode,
  type CreateNutritionistConsultationTypePayload,
  type NutritionistConsultationType,
} from '@/lib/api/consultationTypes';
import { getNutritionistProfile } from '@/lib/api/auth';
import { extractErrorMessage, showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import InlineFeedback from '@/components/shared/InlineFeedback';

type PlanFormState = {
  name: string;
  description: string;
};

type ProgrammeFormState = {
  name: string;
  description: string;
  type: string;
  pricingType: ProgrammePricingType;
  numberOfSessions: string;
  sessionDurationMinutes: string;
  rate: string;
  amountPerSession: string;
  deliveryMode: ProgrammeDeliveryMode;
  isActive: boolean;
};

type PlansViewTab = 'all-plans' | 'consultation-types';

type ConsultationTypeFormState = {
  name: string;
  description: string;
  sessionDurationMinutes: string;
  deliveryMode: ConsultationMode;
  rate: string;
  isActive: boolean;
};

const EMPTY_PLAN_FORM: PlanFormState = {
  name: '',
  description: '',
};

const EMPTY_PROGRAMME_FORM: ProgrammeFormState = {
  name: '',
  description: '',
  type: 'fixed_duration',
  pricingType: 'total_programme_price',
  numberOfSessions: '10',
  sessionDurationMinutes: '60',
  rate: '',
  amountPerSession: '',
  deliveryMode: 'both',
  isActive: true,
};

const DELIVERY_MODE_LABELS: Record<ProgrammeDeliveryMode, string> = {
  online: 'Online',
  in_person: 'In-person',
  both: 'Both Online and In-person',
};

const DELIVERY_MODE_SHORT_LABELS: Record<ProgrammeDeliveryMode, string> = {
  online: 'Online',
  in_person: 'In-person',
  both: 'Both',
};

const PROGRAMME_PRICING_LABELS: Record<ProgrammePricingType, string> = {
  total_programme_price: 'Total plan price',
  price_per_session: 'Price per session',
  free: 'Free',
};

const PROGRAMME_PRICING_HELPERS: Record<ProgrammePricingType, string> = {
  total_programme_price: 'The client pays one total amount for the full plan.',
  price_per_session: 'The client is charged this amount for each session. The total is calculated based on sessions included.',
  free: 'Use for discovery calls or complimentary sessions.',
};

const PROGRAMME_PRICING_OPTIONS: ProgrammePricingType[] = [
  'total_programme_price',
  'price_per_session',
  'free',
];

const PROGRAMME_TYPE_OPTIONS = [
  { value: 'fixed_duration', label: 'Plan' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'free_consultation', label: 'Free Consultation' },
];

const PRICING_TYPE_HELPER = 'Choose how the client will be charged for this plan.';

type FieldLabelProps = {
  children: React.ReactNode;
  className?: string;
  htmlFor?: string;
  tooltip?: string;
};

const FieldLabel: React.FC<FieldLabelProps> = ({ children, className, htmlFor, tooltip }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className={className}>
      {children}
    </Label>
    {tooltip ? (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`${children} help`}
              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#7A8794] hover:text-[#4B5563]"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="z-[10060] max-w-[260px] text-xs leading-relaxed">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : null}
  </div>
);

type PricingTypeSelectProps = {
  disabled?: boolean;
  onChange: (value: ProgrammePricingType) => void;
  value: ProgrammePricingType;
};

const PricingTypeSelect: React.FC<PricingTypeSelectProps> = ({ disabled = false, onChange, value }) => (
  <Select
    disabled={disabled}
    value={value}
    onValueChange={(nextValue) => onChange(nextValue as ProgrammePricingType)}
  >
    <SelectTrigger>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {PROGRAMME_PRICING_OPTIONS.map((pricingType) => (
        <SelectItem key={pricingType} value={pricingType}>
          {PROGRAMME_PRICING_LABELS[pricingType]}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const CREATE_PLAN_TEXT_FIELD_SX = {
  '& .MuiInputLabel-root': { fontSize: '0.875rem', lineHeight: 1.25 },
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
    top: '50%',
    transform: 'translate(14px, -50%) scale(1)',
  },
  '& .MuiInputBase-input': { fontSize: '1rem' },
};

const CREATE_PACKAGE_STEP_CIRCLE_SIZE = 18;

const CREATE_PACKAGE_STEP_CIRCLE_SX = {
  width: CREATE_PACKAGE_STEP_CIRCLE_SIZE,
  height: CREATE_PACKAGE_STEP_CIRCLE_SIZE,
  minWidth: CREATE_PACKAGE_STEP_CIRCLE_SIZE,
  flex: `0 0 ${CREATE_PACKAGE_STEP_CIRCLE_SIZE}px`,
  borderRadius: '999px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.75rem',
  fontWeight: 700,
  lineHeight: 1,
  boxSizing: 'border-box',
};

const EMPTY_CONSULTATION_TYPE_FORM: ConsultationTypeFormState = {
  name: '',
  description: '',
  sessionDurationMinutes: '60',
  deliveryMode: 'both',
  rate: '',
  isActive: true,
};

const toFiniteNumber = (value: string | number | null | undefined): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const getCurrencySymbol = (currencyCode: string): string => {
  const normalizedCurrency = currencyCode.trim().toUpperCase() || 'USD';

  try {
    const currencyPart = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalizedCurrency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value;

    return currencyPart || normalizedCurrency;
  } catch {
    return normalizedCurrency;
  }
};

const formatMoney = (value: string | number | null | undefined, currencySymbol: string): string => {
  const amount = toFiniteNumber(value);
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });

  return `${currencySymbol} ${formatted} total`;
};

const formatCurrencyAmount = (value: string | number | null | undefined, currencySymbol: string): string => {
  const amount = toFiniteNumber(value);
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });

  return `${currencySymbol} ${formatted}`;
};

const formatSessions = (value: number): string => {
  const sessions = toFiniteNumber(value);
  return `${sessions} ${sessions === 1 ? 'session' : 'sessions'}`;
};

const formatDuration = (value: number): string => `${toFiniteNumber(value)} min each`;
const formatRateInline = (value: string | number | null | undefined, currencySymbol: string): string => `${currencySymbol}${toFiniteNumber(value)}`;

const getPerSessionFormTotal = (form: ProgrammeFormState): number =>
  toFiniteNumber(form.numberOfSessions) * toFiniteNumber(form.amountPerSession);

type PerSessionTotalSummaryProps = {
  currencySymbol: string;
  form: ProgrammeFormState;
};

const PerSessionTotalSummary: React.FC<PerSessionTotalSummaryProps> = ({ currencySymbol, form }) => {
  const sessions = toFiniteNumber(form.numberOfSessions);
  const amountPerSession = toFiniteNumber(form.amountPerSession);

  if (sessions <= 0 || amountPerSession <= 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-[#BDEAF6] bg-[#F3FCFF] px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-[#3B4754]">Total amount</span>
        <span className="text-sm font-semibold text-[#148CB4]">
          {formatCurrencyAmount(getPerSessionFormTotal(form), currencySymbol)}
        </span>
      </div>
      <p className="mt-0.5 text-xs leading-snug text-[#6A7282]">
        {sessions} {sessions === 1 ? 'session' : 'sessions'} x {formatCurrencyAmount(amountPerSession, currencySymbol)} per session
      </p>
    </div>
  );
};

const getProgrammeTotal = (programme: Pick<NutritionistProgramme, 'pricingType' | 'numberOfSessions' | 'pricePerSession' | 'rate' | 'totalPrice'>): number => {
  if (programme.pricingType === 'free') {
    return 0;
  }

  if (programme.pricingType === 'price_per_session') {
    return toFiniteNumber(programme.numberOfSessions) * toFiniteNumber(programme.pricePerSession);
  }

  return toFiniteNumber(programme.totalPrice || programme.rate);
};

const formatProgrammePrice = (programme: NutritionistProgramme, currencySymbol: string): string => {
  if (programme.pricingType === 'free') {
    return 'Free';
  }

  if (programme.pricingType === 'price_per_session') {
    return `${formatRateInline(programme.pricePerSession, currencySymbol)} / session (${formatMoney(getProgrammeTotal(programme), currencySymbol)})`;
  }

  return formatMoney(getProgrammeTotal(programme), currencySymbol);
};

const getPlanTotal = (plan: NutritionistPlan, currencySymbol: string): string => {
  if (plan.total !== null && plan.total !== undefined && String(plan.total).trim() !== '') {
    return formatMoney(plan.total, currencySymbol);
  }

  const total = plan.programmes.reduce((sum, programme) => sum + getProgrammeTotal(programme), 0);
  return formatMoney(total, currencySymbol);
};

const hasProgrammeDraft = (form: ProgrammeFormState): boolean =>
  Boolean(
    form.name.trim() ||
    form.description.trim() ||
    form.rate.trim() ||
    form.amountPerSession.trim(),
  );

const normalizeProgrammeFormType = (
  value: string | null | undefined,
  pricingType?: ProgrammePricingType | null,
): string => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[-_\s]+/g, ' ');

  if (normalized === 'free consultation' || (normalized === 'consultation' && pricingType === 'free')) {
    return 'free_consultation';
  }

  if (normalized === 'consultation') {
    return 'consultation';
  }

  return 'fixed_duration';
};

const programmeToForm = (programme?: NutritionistProgramme | null): ProgrammeFormState => {
  if (!programme) {
    return EMPTY_PROGRAMME_FORM;
  }

  return {
    name: programme.name,
    description: programme.description,
    type: normalizeProgrammeFormType(programme.type, programme.pricingType),
    pricingType: programme.pricingType || 'total_programme_price',
    numberOfSessions: String(programme.numberOfSessions || 10),
    sessionDurationMinutes: String(programme.sessionDurationMinutes || 60),
    rate: programme.totalPrice || programme.rate ? String(programme.totalPrice || programme.rate) : '',
    amountPerSession: programme.pricePerSession ? String(programme.pricePerSession) : '',
    deliveryMode: programme.deliveryMode || 'both',
    isActive: programme.isActive,
  };
};

const buildProgrammePayload = (form: ProgrammeFormState): CreateNutritionistProgrammePayload => {
  const numberOfSessions = toFiniteNumber(form.numberOfSessions);
  const pricingType = form.type === 'free_consultation' ? 'free' : form.pricingType;
  const pricePerSession = pricingType === 'price_per_session' ? toFiniteNumber(form.amountPerSession) : 0;
  const totalPrice =
    pricingType === 'price_per_session'
      ? numberOfSessions * pricePerSession
      : pricingType === 'total_programme_price'
        ? toFiniteNumber(form.rate)
        : 0;

  return {
    name: form.name.trim(),
    description: form.description.trim(),
    type: form.type === 'free_consultation' ? 'consultation' : form.type || 'fixed_duration',
    pricingType,
    numberOfSessions,
    sessionsIncluded: numberOfSessions,
    sessionDurationMinutes: toFiniteNumber(form.sessionDurationMinutes),
    durationPerSessionMinutes: toFiniteNumber(form.sessionDurationMinutes),
    rate: totalPrice,
    totalPrice,
    pricePerSession,
    amountPerSession: pricePerSession,
    deliveryMode: form.deliveryMode,
    isActive: form.isActive,
  };
};

const applyPricingTypeChange = (
  form: ProgrammeFormState,
  pricingType: ProgrammePricingType,
): ProgrammeFormState => {
  const nextType =
    pricingType === 'free' && form.type === 'consultation'
      ? 'free_consultation'
      : pricingType !== 'free' && form.type === 'free_consultation'
        ? 'consultation'
        : form.type;

  return {
    ...form,
    type: nextType,
    pricingType,
    rate: pricingType === 'total_programme_price' ? form.rate : '',
    amountPerSession: pricingType === 'price_per_session' ? form.amountPerSession : '',
  };
};

const applyProgrammeTypeChange = (
  form: ProgrammeFormState,
  type: string,
): ProgrammeFormState => {
  if (type === 'free_consultation') {
    return applyPricingTypeChange({ ...form, type: 'consultation' }, 'free');
  }

  if (form.type === 'free_consultation' && type === 'consultation') {
    return applyPricingTypeChange({ ...form, type }, 'total_programme_price');
  }

  return { ...form, type };
};

const buildConsultationTypePayload = (
  form: ConsultationTypeFormState,
): CreateNutritionistConsultationTypePayload => ({
  name: form.name.trim(),
  description: form.description.trim(),
  durationMinutes: toFiniteNumber(form.sessionDurationMinutes),
  mode: form.deliveryMode,
  rate: toFiniteNumber(form.rate),
  isActive: form.isActive,
});

type DialogFeedback = {
  variant: 'error' | 'warning' | 'success' | 'info';
  title?: string;
  message: string;
};

const getPlanFormError = (form: PlanFormState): DialogFeedback | null => {
  if (!form.name.trim()) {
    return { variant: 'warning', title: 'Package name required', message: 'Enter a package name before continuing.' };
  }

  return null;
};

const getProgrammeFormError = (form: ProgrammeFormState): DialogFeedback | null => {
  if (!form.name.trim()) {
    return { variant: 'warning', title: 'Plan name required', message: 'Enter a plan name before saving.' };
  }

  if (toFiniteNumber(form.numberOfSessions) <= 0) {
    return { variant: 'warning', title: 'Invalid sessions', message: 'Enter a valid number of sessions.' };
  }

  if (toFiniteNumber(form.sessionDurationMinutes) <= 0) {
    return { variant: 'warning', title: 'Invalid duration', message: 'Enter a valid session duration.' };
  }

  const pricingType = form.type === 'free_consultation' ? 'free' : form.pricingType;

  if (pricingType === 'total_programme_price' && toFiniteNumber(form.rate) <= 0) {
    return { variant: 'warning', title: 'Invalid plan price', message: 'Enter a valid total plan price.' };
  }

  if (pricingType === 'price_per_session' && toFiniteNumber(form.amountPerSession) <= 0) {
    return { variant: 'warning', title: 'Invalid session price', message: 'Enter a valid amount per session.' };
  }

  return null;
};

const getConsultationTypeFormError = (form: ConsultationTypeFormState): DialogFeedback | null => {
  if (!form.name.trim()) {
    return { variant: 'warning', title: 'Consultation name required', message: 'Enter a consultation name before saving.' };
  }

  if (!form.description.trim()) {
    return { variant: 'warning', title: 'Consultation description required', message: 'Enter a consultation description before saving.' };
  }

  if (toFiniteNumber(form.sessionDurationMinutes) <= 0) {
    return { variant: 'warning', title: 'Invalid duration', message: 'Choose a valid consultation duration.' };
  }

  if (!form.rate.trim() || toFiniteNumber(form.rate) < 0) {
    return { variant: 'warning', title: 'Invalid price', message: 'Enter 0 or a valid consultation price.' };
  }

  return null;
};

type RemoveDialogProps = {
  open: boolean;
  title: string;
  message: string;
  isSubmitting?: boolean;
  feedback?: DialogFeedback | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const RemoveDialog: React.FC<RemoveDialogProps> = ({
  open,
  title,
  message,
  isSubmitting = false,
  feedback,
  onCancel,
  onConfirm,
}) => (
  <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && !isSubmitting && onCancel()}>
    <DialogContent aria-describedby={undefined} className="w-[92vw] max-w-[360px] rounded-lg border-0 bg-white p-0 [&>button]:hidden">
      <DialogTitle className="sr-only">{title}</DialogTitle>
      <DialogDescription className="sr-only">{message}</DialogDescription>
      <Box sx={{ px: 2.5, py: 3, display: 'grid', gap: 2, placeItems: 'center' }}>
        <Box sx={{ display: 'grid', gap: 1.25, placeItems: 'center' }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '8px', bgcolor: 'rgba(255,133,133,0.11)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trash2 size={22} color="#F48A8A" />
          </Box>
          <Box sx={{ display: 'grid', gap: 0.5, placeItems: 'center' }}>
            <Typography sx={{ color: '#000', fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.25 }}>{title}</Typography>
            <Typography sx={{ color: '#7C7C7C', fontSize: '0.8125rem', textAlign: 'center', lineHeight: 1.45, maxWidth: 300 }}>
              {message}
            </Typography>
          </Box>
        </Box>

        {feedback ? (
          <InlineFeedback
            variant={feedback.variant}
            title={feedback.title}
            message={feedback.message}
          />
        ) : null}

        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <MuiButton
            type="button"
            onClick={onCancel}
            variant="contained"
            size="small"
            disabled={isSubmitting}
            sx={{
              flex: 1,
              minHeight: 32,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 500,
              bgcolor: '#20AEDC',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1899C3', boxShadow: 'none' },
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            type="button"
            onClick={onConfirm}
            variant="outlined"
            size="small"
            disabled={isSubmitting}
            sx={{
              flex: 1,
              minHeight: 32,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: '#364153',
              borderColor: '#D1D5DC',
            }}
          >
            {isSubmitting ? 'Removing...' : 'Yes, remove'}
          </MuiButton>
        </Box>
      </Box>
    </DialogContent>
  </Dialog>
);

type PlanCardProps = {
  plan: NutritionistPlan;
  isBusy: boolean;
  currencySymbol: string;
  onEditPlan: (plan: NutritionistPlan) => void;
  onDeletePlan: (plan: NutritionistPlan) => void;
  onAddProgramme: (plan: NutritionistPlan) => void;
  onEditProgramme: (plan: NutritionistPlan, programme: NutritionistProgramme) => void;
  onDeleteProgramme: (plan: NutritionistPlan, programme: NutritionistProgramme) => void;
};

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isBusy,
  currencySymbol,
  onEditPlan,
  onDeletePlan,
  onAddProgramme,
  onEditProgramme,
  onDeleteProgramme,
}) => (
  <div className="rounded-xl border border-[#DCE2E8] bg-white p-4 shadow-[0px_1px_2px_rgba(16,24,40,0.06)] sm:p-5">
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-xl font-semibold leading-tight text-[#202B33] sm:text-[1.35rem]">{plan.name}</h3>
          <span className="rounded-full bg-[#D7E8F0] px-2 py-0.5 text-xs font-semibold text-[#2B4A5D]">{getPlanTotal(plan, currencySymbol)}</span>
        </div>
        <p className="mt-1.5 text-sm text-[#4C5661] sm:text-[15px]">{plan.description}</p>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          disabled={isBusy || !plan.id}
          onClick={() => onEditPlan(plan)}
          className="h-8 rounded-md bg-[#20AEDC] px-3 text-xs font-semibold text-white hover:bg-[#1899C3] sm:px-4 sm:text-sm"
        >
          Edit
        </Button>
        <button
          type="button"
          aria-label="Delete package"
          disabled={isBusy || !plan.id}
          onClick={() => onDeletePlan(plan)}
          className="text-[#FF9A9A] hover:text-[#F48282] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>

    <div className="mt-4 space-y-3">
      {plan.programmes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#E6EBF0] bg-white px-3 py-3 text-sm text-[#6E7A86] sm:px-4">
          No plans added yet.
        </div>
      ) : (
        plan.programmes.map((programme) => (
          <div
            key={programme.id || `${plan.id}-${programme.name}`}
            className="relative overflow-hidden rounded-lg border border-[#E6EBF0] bg-white px-3 py-3 sm:px-4"
          >
            <span
              className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-[#7fd0ef]"
              aria-hidden
            />
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-base font-semibold leading-tight text-[#202B33] sm:text-lg">{programme.name}</p>
                  <span className={`rounded-full px-1.5 py-0.5 text-xs font-semibold text-white ${programme.isActive ? 'bg-[#FAA900]' : 'bg-[#9CA3AF]'}`}>
                    {programme.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-[#4D5966]">{programme.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isBusy || !plan.id || !programme.id}
                  onClick={() => onEditProgramme(plan, programme)}
                  className="h-8 rounded-md border-[#D4DDE5] px-3 text-xs font-medium text-[#55606C] sm:px-4 sm:text-sm"
                >
                  Edit
                </Button>
                <button
                  type="button"
                  aria-label="Delete plan"
                  disabled={isBusy || !plan.id || !programme.id}
                  onClick={() => onDeleteProgramme(plan, programme)}
                  className="text-[#FF9A9A] hover:text-[#F48282] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#6E7A86] sm:text-[13px]">
              <span className="inline-flex items-center gap-1 text-[#43B5E3]">
                <UserRound className="h-3 w-3" />
                {formatSessions(programme.numberOfSessions)}
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-[#F4C35F]" />
                {formatDuration(programme.sessionDurationMinutes)}
              </span>
              <span className="inline-flex items-center gap-1 text-[#4AAE86]">
                <span className="h-2 w-2 rounded-full bg-[#4AAE86]" />
                {formatProgrammePrice(programme, currencySymbol)}
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-[#43B5E3]" />
                {DELIVERY_MODE_LABELS[programme.deliveryMode]}
              </span>
            </div>
          </div>
        ))
      )}
    </div>

    <button
      type="button"
      disabled={isBusy || !plan.id}
      onClick={() => onAddProgramme(plan)}
      className="mt-4 flex h-10 w-full items-center justify-center rounded-lg border border-dashed border-[#9EDAF3] text-sm font-semibold text-[#20AEDC] hover:bg-[#F7FCFF] disabled:cursor-not-allowed disabled:opacity-60"
    >
      + Add Plan
    </button>
  </div>
);

export const PlansAndBillingView = () => {
  const [activeTab, setActiveTab] = useState<PlansViewTab>('all-plans');
  const [profileCurrency, setProfileCurrency] = useState('USD');
  const [plans, setPlans] = useState<NutritionistPlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState('');
  const [consultationTypes, setConsultationTypes] = useState<NutritionistConsultationType[]>([]);
  const [isLoadingConsultationTypes, setIsLoadingConsultationTypes] = useState(false);
  const [hasLoadedConsultationTypes, setHasLoadedConsultationTypes] = useState(false);
  const [consultationTypesError, setConsultationTypesError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddConsultationTypeOpen, setIsAddConsultationTypeOpen] = useState(false);
  const [isEditConsultationTypeOpen, setIsEditConsultationTypeOpen] = useState(false);
  const [isAddProgrammeOpen, setIsAddProgrammeOpen] = useState(false);
  const [programmeDialogMode, setProgrammeDialogMode] = useState<'add' | 'edit'>('add');
  const [isCreatePlanOpen, setIsCreatePlanOpen] = useState(false);
  const [isEditPlanOpen, setIsEditPlanOpen] = useState(false);
  const [isRemovePlanOpen, setIsRemovePlanOpen] = useState(false);
  const [isRemoveProgrammeOpen, setIsRemoveProgrammeOpen] = useState(false);
  const [isRemoveConsultationTypeOpen, setIsRemoveConsultationTypeOpen] = useState(false);
  const [createPlanStep, setCreatePlanStep] = useState<1 | 2>(1);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [selectedProgrammeId, setSelectedProgrammeId] = useState('');
  const [selectedProgrammeTitle, setSelectedProgrammeTitle] = useState('');
  const [selectedConsultationTypeId, setSelectedConsultationTypeId] = useState('');
  const [selectedConsultationTypeTitle, setSelectedConsultationTypeTitle] = useState('');
  const [createPlanForm, setCreatePlanForm] = useState<PlanFormState>(EMPTY_PLAN_FORM);
  const [editPlanForm, setEditPlanForm] = useState<PlanFormState>(EMPTY_PLAN_FORM);
  const [programmeForm, setProgrammeForm] = useState<ProgrammeFormState>(EMPTY_PROGRAMME_FORM);
  const [createProgrammeForms, setCreateProgrammeForms] = useState<ProgrammeFormState[]>([EMPTY_PROGRAMME_FORM]);
  const [consultationTypeForm, setConsultationTypeForm] = useState(EMPTY_CONSULTATION_TYPE_FORM);
  const [editConsultationTypeContext, setEditConsultationTypeContext] = useState<{
    typeId: string;
  }>({ typeId: '' });
  const [dialogFeedback, setDialogFeedback] = useState<DialogFeedback | null>(null);

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId) || null,
    [plans, selectedPlanId],
  );
  const currencySymbol = useMemo(() => getCurrencySymbol(profileCurrency), [profileCurrency]);
  const rateLabel = useMemo(() => `Price (${currencySymbol})`, [currencySymbol]);
  const totalProgrammePriceLabel = useMemo(() => `Total plan price (${currencySymbol})`, [currencySymbol]);
  const amountPerSessionLabel = useMemo(() => `Amount per session (${currencySymbol})`, [currencySymbol]);

  const fetchPlans = useCallback(async (options?: { showLoading?: boolean }) => {
    const showLoading = options?.showLoading ?? true;
    if (showLoading) {
      setIsLoadingPlans(true);
    }

    setPlansError('');

    try {
      const nextPlans = await getNutritionistPlans();
      setPlans(nextPlans);
    } catch (error) {
      const message = extractErrorMessage(error);
      setPlansError(message);
      showErrorToast(error, 'Failed to load packages');
    } finally {
      if (showLoading) {
        setIsLoadingPlans(false);
      }
    }
  }, []);

  const fetchConsultationTypes = useCallback(async (options?: { showLoading?: boolean }) => {
    const showLoading = options?.showLoading ?? true;
    if (showLoading) {
      setIsLoadingConsultationTypes(true);
    }

    setConsultationTypesError('');

    try {
      const nextConsultationTypes = await getNutritionistConsultationTypes();
      setConsultationTypes(nextConsultationTypes);
      setHasLoadedConsultationTypes(true);
    } catch (error) {
      const message = extractErrorMessage(error);
      setConsultationTypesError(message);
      setHasLoadedConsultationTypes(true);
      showErrorToast(error, 'Failed to load consultation types');
    } finally {
      if (showLoading) {
        setIsLoadingConsultationTypes(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    let isMounted = true;

    const loadProfileCurrency = async () => {
      try {
        const response = await getNutritionistProfile();
        if (!isMounted) return;

        const nextCurrency = response.data?.currency?.trim().toUpperCase();
        if (nextCurrency) {
          setProfileCurrency(nextCurrency);
        }
      } catch (error) {
        console.error('Failed to load nutritionist currency:', error);
      }
    };

    void loadProfileCurrency();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'consultation-types' && !hasLoadedConsultationTypes) {
      void fetchConsultationTypes();
    }
  }, [activeTab, fetchConsultationTypes, hasLoadedConsultationTypes]);

  const refreshPlans = useCallback(async () => {
    await fetchPlans({ showLoading: false });
  }, [fetchPlans]);

  const refreshConsultationTypes = useCallback(async () => {
    await fetchConsultationTypes({ showLoading: false });
  }, [fetchConsultationTypes]);

  const resetCreatePlanDialog = () => {
    setCreatePlanStep(1);
    setCreatePlanForm(EMPTY_PLAN_FORM);
    setCreateProgrammeForms([EMPTY_PROGRAMME_FORM]);
    setDialogFeedback(null);
  };

  const updateCreateProgrammeForm = (index: number, updates: Partial<ProgrammeFormState>) => {
    setCreateProgrammeForms((prev) => prev.map((form, formIndex) => (formIndex === index ? { ...form, ...updates } : form)));
  };

  const handleOpenCreatePlan = () => {
    resetCreatePlanDialog();
    setIsCreatePlanOpen(true);
  };

  const handleOpenAddProgramme = (plan: NutritionistPlan) => {
    setSelectedPlanId(plan.id);
    setSelectedPlanName(plan.name);
    setSelectedProgrammeId('');
    setSelectedProgrammeTitle('');
    setProgrammeDialogMode('add');
    setProgrammeForm(EMPTY_PROGRAMME_FORM);
    setDialogFeedback(null);
    setIsAddProgrammeOpen(true);
  };

  const handleOpenAddConsultationType = () => {
    setConsultationTypeForm(EMPTY_CONSULTATION_TYPE_FORM);
    setDialogFeedback(null);
    setIsAddConsultationTypeOpen(true);
  };

  const handleSubmitConsultationType = async () => {
    const validationError = getConsultationTypeFormError(consultationTypeForm);
    if (validationError) {
      setDialogFeedback(validationError);
      return;
    }

    setDialogFeedback(null);
    setIsSubmitting(true);
    try {
      await createNutritionistConsultationType(buildConsultationTypePayload(consultationTypeForm));
      showSuccessToast('Consultation type added');
      setIsAddConsultationTypeOpen(false);
      setConsultationTypeForm(EMPTY_CONSULTATION_TYPE_FORM);
      setDialogFeedback(null);
      await refreshConsultationTypes();
    } catch (error) {
      setDialogFeedback({
        variant: 'error',
        title: 'Failed to add consultation type',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditConsultationType = (consultation: NutritionistConsultationType) => {
    setEditConsultationTypeContext({
      typeId: consultation.id,
    });
    setConsultationTypeForm({
      name: consultation.name,
      description: consultation.description,
      sessionDurationMinutes: String(consultation.durationMinutes || 60),
      deliveryMode: consultation.mode || 'both',
      rate: Number.isFinite(consultation.rate) ? String(consultation.rate) : '',
      isActive: consultation.isActive,
    });
    setDialogFeedback(null);
    setIsEditConsultationTypeOpen(true);
  };

  const handleOpenRemoveConsultationType = (consultation: NutritionistConsultationType) => {
    setSelectedConsultationTypeId(consultation.id);
    setSelectedConsultationTypeTitle(consultation.name);
    setDialogFeedback(null);
    setIsRemoveConsultationTypeOpen(true);
  };

  const handleSaveEditedConsultationType = async () => {
    const validationError = getConsultationTypeFormError(consultationTypeForm);
    if (validationError) {
      setDialogFeedback(validationError);
      return;
    }

    if (!editConsultationTypeContext.typeId) {
      setDialogFeedback({
        variant: 'error',
        title: 'Unable to update consultation type',
        message: 'Select a consultation type before saving changes.',
      });
      return;
    }

    setDialogFeedback(null);
    setIsSubmitting(true);
    try {
      await updateNutritionistConsultationType(
        editConsultationTypeContext.typeId,
        buildConsultationTypePayload(consultationTypeForm),
      );
      showSuccessToast('Consultation type updated');
      setIsEditConsultationTypeOpen(false);
      setEditConsultationTypeContext({ typeId: '' });
      setDialogFeedback(null);
      await refreshConsultationTypes();
    } catch (error) {
      setDialogFeedback({
        variant: 'error',
        title: 'Failed to update consultation type',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditProgramme = (plan: NutritionistPlan, programme: NutritionistProgramme) => {
    setSelectedPlanId(plan.id);
    setSelectedPlanName(plan.name);
    setSelectedProgrammeId(programme.id);
    setSelectedProgrammeTitle(programme.name);
    setProgrammeDialogMode('edit');
    setProgrammeForm(programmeToForm(programme));
    setDialogFeedback(null);
    setIsAddProgrammeOpen(true);
  };

  const handleOpenEditPlan = (plan: NutritionistPlan) => {
    setSelectedPlanId(plan.id);
    setSelectedPlanName(plan.name);
    setEditPlanForm({
      name: plan.name,
      description: plan.description,
    });
    setDialogFeedback(null);
    setIsEditPlanOpen(true);
  };

  const handleOpenRemovePlan = (plan: NutritionistPlan) => {
    setSelectedPlanId(plan.id);
    setSelectedPlanName(plan.name);
    setDialogFeedback(null);
    setIsRemovePlanOpen(true);
  };

  const handleConfirmRemovePlan = async () => {
    if (!selectedPlanId) {
      setDialogFeedback({
        variant: 'error',
        title: 'Unable to remove package',
        message: 'Select a package before removing it.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteNutritionistPlan(selectedPlanId);
      setIsRemovePlanOpen(false);
      setSelectedPlanId('');
      setSelectedPlanName('');
      setDialogFeedback(null);
      showSuccessToast('Package removed');
      await refreshPlans();
    } catch (error) {
      setDialogFeedback({
        variant: 'error',
        title: 'Failed to remove package',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenRemoveProgramme = (plan: NutritionistPlan, programme: NutritionistProgramme) => {
    setSelectedPlanId(plan.id);
    setSelectedPlanName(plan.name);
    setSelectedProgrammeId(programme.id);
    setSelectedProgrammeTitle(programme.name);
    setDialogFeedback(null);
    setIsRemoveProgrammeOpen(true);
  };

  const handleConfirmRemoveProgramme = async () => {
    if (!selectedPlanId || !selectedProgrammeId) {
      setDialogFeedback({
        variant: 'error',
        title: 'Unable to remove plan',
        message: 'Select a plan before removing it.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteNutritionistProgramme(selectedPlanId, selectedProgrammeId);
      setIsRemoveProgrammeOpen(false);
      setSelectedProgrammeId('');
      setSelectedProgrammeTitle('');
      setDialogFeedback(null);
      showSuccessToast('Plan removed');
      await refreshPlans();
    } catch (error) {
      setDialogFeedback({
        variant: 'error',
        title: 'Failed to remove plan',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmRemoveConsultationType = async () => {
    if (!selectedConsultationTypeId) {
      setDialogFeedback({
        variant: 'error',
        title: 'Unable to remove consultation type',
        message: 'Select a consultation type before removing it.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteNutritionistConsultationType(selectedConsultationTypeId);
      setIsRemoveConsultationTypeOpen(false);
      setSelectedConsultationTypeId('');
      setSelectedConsultationTypeTitle('');
      setDialogFeedback(null);
      showSuccessToast('Consultation type removed');
      await refreshConsultationTypes();
    } catch (error) {
      setDialogFeedback({
        variant: 'error',
        title: 'Failed to remove consultation type',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitProgramme = async () => {
    const validationError = getProgrammeFormError(programmeForm);
    if (validationError) {
      setDialogFeedback(validationError);
      return;
    }

    if (!selectedPlanId) {
      setDialogFeedback({
        variant: 'error',
        title: 'Unable to save plan',
        message: 'Select a package before saving this plan.',
      });
      return;
    }

    setDialogFeedback(null);
    setIsSubmitting(true);
    try {
      const payload = buildProgrammePayload(programmeForm);
      if (programmeDialogMode === 'edit' && selectedProgrammeId) {
        await updateNutritionistProgramme(selectedPlanId, selectedProgrammeId, payload);
        showSuccessToast('Plan updated');
      } else {
        await createNutritionistProgramme(selectedPlanId, payload);
        showSuccessToast('Plan added');
      }

      setIsAddProgrammeOpen(false);
      setSelectedProgrammeId('');
      setSelectedProgrammeTitle('');
      setDialogFeedback(null);
      await refreshPlans();
    } catch (error) {
      setDialogFeedback({
        variant: 'error',
        title: programmeDialogMode === 'edit' ? 'Failed to update plan' : 'Failed to add plan',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSavePlan = async () => {
    const validationError = getPlanFormError(editPlanForm);
    if (validationError) {
      setDialogFeedback(validationError);
      return;
    }

    if (!selectedPlanId) {
      setDialogFeedback({
        variant: 'error',
        title: 'Unable to update package',
        message: 'Select a package before saving changes.',
      });
      return;
    }

    setDialogFeedback(null);
    setIsSubmitting(true);
    try {
      await updateNutritionistPlan(selectedPlanId, {
        name: editPlanForm.name.trim(),
        description: editPlanForm.description.trim(),
      });
      setIsEditPlanOpen(false);
      setSelectedPlanId('');
      setSelectedPlanName('');
      setDialogFeedback(null);
      showSuccessToast('Package updated');
      await refreshPlans();
    } catch (error) {
      setDialogFeedback({
        variant: 'error',
        title: 'Failed to update package',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreatePlanAction = async () => {
    if (createPlanStep === 1) {
      const validationError = getPlanFormError(createPlanForm);
      if (validationError) {
        setDialogFeedback(validationError);
        return;
      }
      setDialogFeedback(null);
      setCreatePlanStep(2);
      return;
    }

    const planValidationError = getPlanFormError(createPlanForm);
    if (planValidationError) {
      setDialogFeedback(planValidationError);
      setCreatePlanStep(1);
      return;
    }

    const draftProgrammeForms = createProgrammeForms.filter((form) => hasProgrammeDraft(form));
    for (const form of draftProgrammeForms) {
      const validationError = getProgrammeFormError(form);
      if (validationError) {
        setDialogFeedback(validationError);
        return;
      }
    }

    setDialogFeedback(null);
    setIsSubmitting(true);
    try {
      const createdPlan = await createNutritionistPlan({
        name: createPlanForm.name.trim(),
        description: createPlanForm.description.trim(),
      });

      if (draftProgrammeForms.length > 0) {
        let createdPlanId = createdPlan.id;

        if (!createdPlanId) {
          const latestPlans = await getNutritionistPlans();
          setPlans(latestPlans);
          createdPlanId = latestPlans.find((plan) => plan.name === createPlanForm.name.trim())?.id || '';
        }

        if (!createdPlanId) {
          throw new Error('Package created, but the backend did not return a package id for adding the plan.');
        }

        for (const form of draftProgrammeForms) {
          await createNutritionistProgramme(createdPlanId, buildProgrammePayload(form));
        }
      }

      setIsCreatePlanOpen(false);
      resetCreatePlanDialog();
      setDialogFeedback(null);
      showSuccessToast('Package created');
      await refreshPlans();
    } catch (error) {
      setDialogFeedback({
        variant: 'error',
        title: 'Failed to create package',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="p-6 sm:p-7 lg:p-8">
        <div className="flex min-h-[calc(100vh-7.5rem)] flex-col space-y-4 rounded-2xl border border-[#DBE6EB] bg-white p-4 sm:p-5">
          <div className="rounded-lg border border-[#DBE6EB] bg-[#F8FAFC] px-4 py-2">
            <p className="text-sm text-[#6B7280]">
              <RouterLink to="/profile?view=hub" className="hover:text-[#4B5563]">
                Settings
              </RouterLink>
              <span className="px-2">/</span>
              <RouterLink to="/profile?tab=billing" className="text-[#111827] hover:text-[#374151]">
                Packages and billings
              </RouterLink>
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <RouterLink
                to="/profile?view=hub"
                aria-label="Back to settings"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#1F2B34] hover:bg-[#F1F5F9]"
              >
                <ArrowLeft className="h-5 w-5" />
              </RouterLink>
              <h2 className="text-xl font-semibold leading-tight text-[#1F2B34] sm:text-2xl">Client Packages</h2>
            </div>
            <p className="mt-2 text-sm text-[#6C7580]">
              Organize what you offer into packages. Each package can include multiple plans for clients to choose from.
            </p>
          </div>

          <div className="border-b border-[#DEE3E8]">
            <div className="flex gap-8">
              <button
                type="button"
                onClick={() => setActiveTab('all-plans')}
                className={`pb-2 text-sm ${activeTab === 'all-plans' ? 'border-b-2 border-[#20AEDC] font-medium text-[#20AEDC]' : 'font-normal text-[#737A85]'}`}
              >
                All Packages
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('consultation-types')}
                className={`pb-2 text-sm ${activeTab === 'consultation-types' ? 'border-b-2 border-[#20AEDC] font-medium text-[#20AEDC]' : 'font-normal text-[#737A85]'}`}
              >
                Consultation Types
              </button>
            </div>
          </div>

          {activeTab === 'all-plans' ? (
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex w-full items-start gap-2 rounded-md border border-[#5AB6E7] px-4 py-3 md:max-w-[55%]">
                <Lightbulb className="mt-0.5 h-4 w-4 text-[#20AEDC]" />
                <div>
                  <p className="text-xs font-semibold text-[#1F2B34]">What your clients can book</p>
                  <p className="mt-0.5 text-xs text-[#4E5965]">
                    Packages appear on your booking page and help organize your services.
                    <br />
                    Each package can include multiple plans that clients can purchase.
                  </p>
                </div>
              </div>

              <Button
                type="button"
                disabled={isSubmitting}
                onClick={handleOpenCreatePlan}
                className="h-9 rounded-md bg-[#20AEDC] px-4 text-xs font-semibold hover:bg-[#1899C3] sm:px-5 sm:text-sm"
              >
                Create Package +
              </Button>
            </div>
          ) : null}

          {activeTab === 'consultation-types' ? (
            <div className="rounded-[20px] border border-[rgba(0,0,0,0.04)] bg-white px-[22px] py-6 shadow-[0px_4px_4px_rgba(0,0,0,0.07)]">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-black">Consultation Types</h3>
                <Button
                  type="button"
                  disabled={isSubmitting || isLoadingConsultationTypes || !hasLoadedConsultationTypes}
                  onClick={handleOpenAddConsultationType}
                  className="h-8 rounded-[10px] bg-[#20AEDC] px-3 text-sm font-medium text-white hover:bg-[#1899C3]"
                >
                  Add Type +
                </Button>
              </div>
              <div className="mt-6">
                {isLoadingConsultationTypes || !hasLoadedConsultationTypes ? (
                  <div className="flex min-h-[180px] items-center justify-center rounded-xl border border-[#E6EBF0] bg-white">
                    <div className="flex items-center gap-3 text-sm font-medium text-[#6C7580]">
                      <CircularProgress size={22} sx={{ color: '#20AEDC' }} />
                      Loading consultation types...
                    </div>
                  </div>
                ) : consultationTypesError ? (
                  <div className="rounded-xl border border-[#F5C2C7] bg-[#FFF8F8] p-4">
                    <p className="text-sm font-semibold text-[#B42318]">Unable to load consultation types</p>
                    <p className="mt-1 text-sm text-[#7A4B4B]">{consultationTypesError}</p>
                    <Button
                      type="button"
                      onClick={() => void fetchConsultationTypes()}
                      className="mt-3 h-9 rounded-md bg-[#20AEDC] px-4 text-xs font-semibold hover:bg-[#1899C3] sm:text-sm"
                    >
                      Retry
                    </Button>
                  </div>
                ) : consultationTypes.length === 0 ? (
                  <div className="flex min-h-[180px] flex-col items-center justify-center rounded-xl border border-dashed border-[#DCE2E8] bg-white p-6 text-center">
                    <p className="text-sm font-semibold text-[#1F2B34]">No consultation types created yet</p>
                    <p className="mt-1 text-sm text-[#6C7580]">Add a consultation type to make it available on your booking page.</p>
                  </div>
                ) : (
                  consultationTypes.map((consultation, index) => (
                    <div
                      key={consultation.id || consultation.name}
                      className={`flex items-center justify-between py-3 ${
                        index < consultationTypes.length - 1 ? 'border-b border-[#D1D5DC]' : ''
                      }`}
                    >
                      <div className="min-w-0 pr-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-base font-semibold text-black">{consultation.name}</p>
                          <span className={`rounded-full px-1.5 py-0.5 text-xs font-semibold text-white ${consultation.isActive ? 'bg-[#FAA900]' : 'bg-[#9CA3AF]'}`}>
                            {consultation.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {consultation.description ? (
                          <p className="mt-1 text-sm text-[#4D5966]">{consultation.description}</p>
                        ) : null}
                        <p className="mt-1 text-sm text-[#6A7282]">
                          {toFiniteNumber(consultation.durationMinutes)} min · {DELIVERY_MODE_SHORT_LABELS[consultation.mode || 'both']} · {formatRateInline(consultation.rate, currencySymbol)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          disabled={isSubmitting || !consultation.id}
                          onClick={() => handleOpenEditConsultationType(consultation)}
                          className="h-[25px] rounded-[10px] border-[#CFD0D2] px-4 text-xs font-medium text-[#0A0A0A] hover:bg-transparent"
                        >
                          Edit
                        </Button>
                        <button
                          type="button"
                          aria-label={`Delete ${consultation.name}`}
                          disabled={isSubmitting || !consultation.id}
                          onClick={() => handleOpenRemoveConsultationType(consultation)}
                          className="text-[#FF9A9A] hover:text-[#F48282] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : isLoadingPlans ? (
            <div className="flex min-h-[240px] flex-1 items-center justify-center rounded-xl border border-[#E6EBF0] bg-white">
              <div className="flex items-center gap-3 text-sm font-medium text-[#6C7580]">
                <CircularProgress size={22} sx={{ color: '#20AEDC' }} />
                Loading packages...
              </div>
            </div>
          ) : plansError ? (
            <div className="rounded-xl border border-[#F5C2C7] bg-[#FFF8F8] p-4">
              <p className="text-sm font-semibold text-[#B42318]">Unable to load packages</p>
              <p className="mt-1 text-sm text-[#7A4B4B]">{plansError}</p>
              <Button
                type="button"
                onClick={() => void fetchPlans()}
                className="mt-3 h-9 rounded-md bg-[#20AEDC] px-4 text-xs font-semibold hover:bg-[#1899C3] sm:text-sm"
              >
                Retry
              </Button>
            </div>
          ) : plans.length === 0 ? (
            <div className="flex min-h-[240px] flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-[#DCE2E8] bg-white p-6 text-center">
              <p className="text-sm font-semibold text-[#1F2B34]">No packages created yet</p>
              <p className="mt-1 text-sm text-[#6C7580]">Create a package to start adding plans.</p>
            </div>
          ) : (
            plans.map((plan) => (
              <PlanCard
                key={plan.id || plan.name}
                plan={plan}
                isBusy={isSubmitting}
                currencySymbol={currencySymbol}
                onEditPlan={handleOpenEditPlan}
                onDeletePlan={handleOpenRemovePlan}
                onAddProgramme={handleOpenAddProgramme}
                onEditProgramme={handleOpenEditProgramme}
                onDeleteProgramme={handleOpenRemoveProgramme}
              />
            ))
          )}
        </div>
      </div>

      <Dialog open={isAddProgrammeOpen} onOpenChange={(open) => !isSubmitting && setIsAddProgrammeOpen(open)}>
        <DialogContent aria-describedby={undefined} className="!flex max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[560px] flex-col gap-0 overflow-hidden rounded-xl border border-[#F3F4F6] bg-white p-0 sm:max-h-[calc(100dvh-3rem)] sm:w-[92vw] [&>button]:hidden">
          <DialogTitle className="sr-only">
            {programmeDialogMode === 'edit' ? 'Edit Plan' : 'Add Plan'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {programmeDialogMode === 'edit' ? 'Edit plan details.' : 'Add plan details.'}
          </DialogDescription>
          <div className="min-h-0 flex-1 overflow-y-auto border-b border-[#F3F4F6] p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold leading-tight text-[#1E293B] sm:text-xl">
                    {programmeDialogMode === 'edit' ? 'Edit Plan' : 'Add Plan'}
                  </h3>
                  <span className="rounded-full border border-[#6CC6ED] bg-[#F8FDFF] px-2.5 py-0.5 text-xs font-semibold text-[#1A1A1A]">
                    {selectedPlan?.name || selectedPlanName}
                  </span>
                </div>
                <p className="mt-2 text-xs text-[#6A7282] sm:text-sm">Create a plan under this package that clients can purchase or book</p>
              </div>
              <button
                type="button"
                aria-label="Close add plan"
                disabled={isSubmitting}
                onClick={() => setIsAddProgrammeOpen(false)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6] text-[#6B7280] hover:text-[#4B5563] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {dialogFeedback ? (
              <div className="mt-4">
                <InlineFeedback
                  variant={dialogFeedback.variant}
                  title={dialogFeedback.title}
                  message={dialogFeedback.message}
                />
              </div>
            ) : null}

            <div className="mt-4 space-y-3">
              <div className="space-y-2">
                <Label htmlFor="program-name">Plan name *</Label>
                <Input
                  id="program-name"
                  required
                  disabled={isSubmitting}
                  placeholder="Eg.12-Week Metabolism Reset"
                  value={programmeForm.name}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="program-description">Description</Label>
                <Input
                  id="program-description"
                  disabled={isSubmitting}
                  placeholder="What does this appointment involve ?"
                  value={programmeForm.description}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <FieldLabel>Type</FieldLabel>
                  <Select
                    value={programmeForm.type}
                    onValueChange={(value) => setProgrammeForm((prev) => applyProgrammeTypeChange(prev, value))}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROGRAMME_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <FieldLabel tooltip={PRICING_TYPE_HELPER}>Pricing type</FieldLabel>
                  <PricingTypeSelect
                    disabled={isSubmitting}
                    value={programmeForm.pricingType}
                    onChange={(pricingType) => setProgrammeForm((prev) => applyPricingTypeChange(prev, pricingType))}
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <FieldLabel htmlFor="number-of-sessions">Sessions included</FieldLabel>
                  <Input
                    id="number-of-sessions"
                    type="number"
                    required
                    disabled={isSubmitting}
                    value={programmeForm.numberOfSessions}
                    onChange={(event) => setProgrammeForm((prev) => ({ ...prev, numberOfSessions: event.target.value }))}
                  />
                </div>
                {programmeForm.pricingType === 'total_programme_price' ? (
                  <div className="space-y-2">
                    <FieldLabel htmlFor="programme-rate" tooltip={PROGRAMME_PRICING_HELPERS.total_programme_price}>
                      {totalProgrammePriceLabel}
                    </FieldLabel>
                    <Input
                      id="programme-rate"
                      type="number"
                      disabled={isSubmitting}
                      placeholder="Enter Amount"
                      value={programmeForm.rate}
                      onChange={(event) => setProgrammeForm((prev) => ({ ...prev, rate: event.target.value }))}
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <FieldLabel>Duration per session</FieldLabel>
                    <Select
                      value={programmeForm.sessionDurationMinutes}
                      onValueChange={(value) => setProgrammeForm((prev) => ({ ...prev, sessionDurationMinutes: value }))}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 mins</SelectItem>
                        <SelectItem value="45">45 mins</SelectItem>
                        <SelectItem value="60">60 mins</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {programmeForm.pricingType === 'price_per_session' ? (
                <div className="space-y-2">
                  <FieldLabel htmlFor="programme-amount-per-session" tooltip={PROGRAMME_PRICING_HELPERS.price_per_session}>
                    {amountPerSessionLabel}
                  </FieldLabel>
                  <Input
                    id="programme-amount-per-session"
                    type="number"
                    disabled={isSubmitting}
                    placeholder="Enter Amount"
                    value={programmeForm.amountPerSession}
                    onChange={(event) => setProgrammeForm((prev) => ({ ...prev, amountPerSession: event.target.value }))}
                  />
                  <PerSessionTotalSummary form={programmeForm} currencySymbol={currencySymbol} />
                </div>
              ) : null}

              <div className="flex items-center gap-3 pt-1">
                <span className="text-sm font-medium text-[#0A0A0A]">Make it Active</span>
                <Switch
                  checked={programmeForm.isActive}
                  disabled={isSubmitting}
                  onCheckedChange={(checked) => setProgrammeForm((prev) => ({ ...prev, isActive: checked }))}
                />
              </div>
            </div>
          </div>

          <div className="flex shrink-0 justify-end border-t border-[#F3F4F6] px-4 py-3 sm:px-5">
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={() => void handleSubmitProgramme()}
              className="h-9 w-full rounded-lg bg-[#20AEDC] px-4 text-xs font-semibold text-white hover:bg-[#1899C3] sm:w-auto sm:text-sm"
            >
              {isSubmitting ? 'Saving...' : 'Save plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddConsultationTypeOpen} onOpenChange={(open) => !isSubmitting && setIsAddConsultationTypeOpen(open)}>
        <DialogContent aria-describedby={undefined} className="!flex max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[860px] flex-col gap-0 overflow-hidden rounded-2xl border border-[#F3F4F6] bg-white p-0 sm:max-h-[calc(100dvh-3rem)] sm:w-[92vw] [&>button]:hidden">
          <DialogTitle className="sr-only">Add Consultation Type</DialogTitle>
          <DialogDescription className="sr-only">Create a consultation type to include in client packages and plans.</DialogDescription>
          <div className="min-h-0 flex-1 overflow-y-auto border-b border-[#F3F4F6] px-4 py-5 sm:px-8 sm:py-7">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-2xl font-semibold leading-[1.05] text-[#101828] sm:text-[2rem]">Add Consultation Type</h3>
                <p className="mt-2 text-sm text-[#6A7282]">Create a consultation type to include in client packages and plans</p>
              </div>
              <button
                type="button"
                aria-label="Close add consultation type"
                disabled={isSubmitting}
                onClick={() => setIsAddConsultationTypeOpen(false)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6] text-[#6B7280] hover:text-[#4B5563] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {dialogFeedback ? (
              <div className="mt-4">
                <InlineFeedback
                  variant={dialogFeedback.variant}
                  title={dialogFeedback.title}
                  message={dialogFeedback.message}
                />
              </div>
            ) : null}

            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="consultation-name">Consultation name *</Label>
                <Input
                  id="consultation-name"
                  required
                  disabled={isSubmitting}
                  placeholder="Eg. Free Consultation"
                  value={consultationTypeForm.name}
                  onChange={(event) => setConsultationTypeForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="consultation-description">Description *</Label>
                <Input
                  id="consultation-description"
                  required
                  disabled={isSubmitting}
                  placeholder="What does this appointment involve ?"
                  value={consultationTypeForm.description}
                  onChange={(event) => setConsultationTypeForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Select
                    value={consultationTypeForm.sessionDurationMinutes}
                    onValueChange={(value) => setConsultationTypeForm((prev) => ({ ...prev, sessionDurationMinutes: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">60 min</SelectItem>
                      <SelectItem value="75">75 min</SelectItem>
                      <SelectItem value="90">90 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select
                    value={consultationTypeForm.deliveryMode}
                    onValueChange={(value) => setConsultationTypeForm((prev) => ({ ...prev, deliveryMode: value as ConsultationMode }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="in_person">In-person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="consultation-rate">{rateLabel}</Label>
                <Input
                  id="consultation-rate"
                  type="number"
                  disabled={isSubmitting}
                  placeholder="Enter Amount"
                  value={consultationTypeForm.rate}
                  onChange={(event) => setConsultationTypeForm((prev) => ({ ...prev, rate: event.target.value }))}
                />
              </div>

            </div>
          </div>
          <div className="flex shrink-0 justify-end border-t border-[#F3F4F6] px-4 py-3 sm:px-8 sm:py-4">
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={() => void handleSubmitConsultationType()}
              className="h-[41px] w-full rounded-[14px] bg-[#20AEDC] px-8 text-sm font-semibold text-white hover:bg-[#1899C3] sm:w-auto"
            >
              {isSubmitting ? 'Saving...' : 'Save consultation type'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditConsultationTypeOpen} onOpenChange={(open) => !isSubmitting && setIsEditConsultationTypeOpen(open)}>
        <DialogContent aria-describedby={undefined} className="!flex max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[860px] flex-col gap-0 overflow-hidden rounded-2xl border border-[#F3F4F6] bg-white p-0 sm:max-h-[calc(100dvh-3rem)] sm:w-[92vw] [&>button]:hidden">
          <DialogTitle className="sr-only">Edit Consultation Type</DialogTitle>
          <DialogDescription className="sr-only">Update the consultation type for your booking page.</DialogDescription>
          <div className="min-h-0 flex-1 overflow-y-auto border-b border-[#F3F4F6] px-4 py-5 sm:px-8 sm:py-7">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-2xl font-semibold leading-[1.05] text-[#101828] sm:text-[2rem]">Edit Consultation Type</h3>
                <p className="mt-2 text-sm text-[#6A7282]">Update the consultation type for your booking page</p>
              </div>
              <button
                type="button"
                aria-label="Close edit consultation type"
                disabled={isSubmitting}
                onClick={() => setIsEditConsultationTypeOpen(false)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6] text-[#6B7280] hover:text-[#4B5563] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {dialogFeedback ? (
              <div className="mt-4">
                <InlineFeedback
                  variant={dialogFeedback.variant}
                  title={dialogFeedback.title}
                  message={dialogFeedback.message}
                />
              </div>
            ) : null}

            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-consultation-name">Consultation name *</Label>
                <Input
                  id="edit-consultation-name"
                  required
                  disabled={isSubmitting}
                  value={consultationTypeForm.name}
                  onChange={(event) => setConsultationTypeForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-consultation-description">Description *</Label>
                <textarea
                  id="edit-consultation-description"
                  required
                  disabled={isSubmitting}
                  value={consultationTypeForm.description}
                  onChange={(event) => setConsultationTypeForm((prev) => ({ ...prev, description: event.target.value }))}
                  className="min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Select
                    value={consultationTypeForm.sessionDurationMinutes}
                    onValueChange={(value) => setConsultationTypeForm((prev) => ({ ...prev, sessionDurationMinutes: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">60 min</SelectItem>
                      <SelectItem value="75">75 min</SelectItem>
                      <SelectItem value="90">90 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select
                    value={consultationTypeForm.deliveryMode}
                    onValueChange={(value) => setConsultationTypeForm((prev) => ({ ...prev, deliveryMode: value as ConsultationMode }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="in_person">In-person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-consultation-rate">{rateLabel}</Label>
                <Input
                  id="edit-consultation-rate"
                  type="number"
                  disabled={isSubmitting}
                  value={consultationTypeForm.rate}
                  onChange={(event) => setConsultationTypeForm((prev) => ({ ...prev, rate: event.target.value }))}
                />
              </div>

            </div>
          </div>
          <div className="flex shrink-0 justify-end border-t border-[#F3F4F6] px-4 py-3 sm:px-8 sm:py-4">
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={() => void handleSaveEditedConsultationType()}
              className="h-[41px] w-full rounded-[14px] bg-[#20AEDC] px-8 text-sm font-semibold text-white hover:bg-[#1899C3] sm:w-auto"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCreatePlanOpen}
        onOpenChange={(open) => {
          if (!isSubmitting) {
            setIsCreatePlanOpen(open);
          }
        }}
      >
        <DialogContent aria-describedby={undefined} className="!flex max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[560px] flex-col gap-0 overflow-hidden rounded-xl border border-[#F3F4F6] bg-white p-0 sm:max-h-[calc(100dvh-3rem)] sm:w-[92vw] [&>button]:hidden">
          <DialogTitle className="sr-only">Create Package</DialogTitle>
          <DialogDescription className="sr-only">
            Create a package and optionally add a plan with type and session details.
          </DialogDescription>
          <Box
            sx={{
              borderBottom: '1px solid #F3F4F6',
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              p: { xs: 2, sm: 2.5 },
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#101828', fontWeight: 600, fontSize: { xs: '1.125rem', sm: '1.25rem' }, lineHeight: 1.2 }}>
                  Create Package
                </Typography>
                <Typography sx={{ mt: 0.75, color: '#6A7282', fontSize: '0.8125rem' }}>
                  Define a new nutritional roadmap for your practice.
                </Typography>
              </Box>
              <IconButton
                aria-label="Close create package"
                onClick={() => setIsCreatePlanOpen(false)}
                size="small"
                disabled={isSubmitting}
                sx={{ width: 28, height: 28, borderRadius: '999px', bgcolor: '#F3F4F6', color: '#6B7280' }}
              >
                <X size={14} />
              </IconButton>
            </Box>

            <Box sx={{ mt: 1.75, minHeight: 46, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, borderBottom: '1px solid #F3F4F6', pb: 1.25 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: '#20AEDC', fontSize: '1rem', fontWeight: 600 }}>
                <Box sx={{ ...CREATE_PACKAGE_STEP_CIRCLE_SX, bgcolor: '#20AEDC', color: '#fff' }}>
                  1
                </Box>
                Create Package
              </Box>
              <Typography sx={{ color: '#D1D5DB', fontSize: '1rem' }}>{'>'}</Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: createPlanStep === 2 ? '#20AEDC' : '#99A1AF', fontSize: '1rem', fontWeight: createPlanStep === 2 ? 600 : 400 }}>
                <Box
                  sx={{
                    ...CREATE_PACKAGE_STEP_CIRCLE_SX,
                    bgcolor: createPlanStep === 2 ? '#20AEDC' : '#F3F4F6',
                    color: createPlanStep === 2 ? '#fff' : '#99A1AF',
                  }}
                >
                  2
                </Box>
                Add plans
              </Box>
            </Box>

            {dialogFeedback ? (
              <Box sx={{ mt: 1.75 }}>
                <InlineFeedback
                  variant={dialogFeedback.variant}
                  title={dialogFeedback.title}
                  message={dialogFeedback.message}
                />
              </Box>
            ) : null}

            {createPlanStep === 1 ? (
              <Box sx={{ mt: 1.75, display: 'grid', gap: 1.5 }}>
                <TextField
                  fullWidth
                  required
                  disabled={isSubmitting}
                  label="Package Name"
                  placeholder="Eg.12-Week Transformation"
                  size="small"
                  sx={CREATE_PLAN_TEXT_FIELD_SX}
                  value={createPlanForm.name}
                  onChange={(event) => setCreatePlanForm((prev) => ({ ...prev, name: event.target.value }))}
                />
                <TextField
                  fullWidth
                  disabled={isSubmitting}
                  label="Description"
                  placeholder="What does this package involve ?"
                  multiline
                  minRows={3}
                  size="small"
                  sx={CREATE_PLAN_TEXT_FIELD_SX}
                  value={createPlanForm.description}
                  onChange={(event) => setCreatePlanForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </Box>
            ) : (
              <Box sx={{ mt: 1.75, display: 'grid', gap: 1.5 }}>
                {createProgrammeForms.map((programmeDraft, index) => (
                  <Box key={`programme-draft-${index}`} sx={{ display: 'grid', gap: 1.5, borderTop: index === 0 ? 'none' : '1px solid #F3F4F6', pt: index === 0 ? 0 : 1.5 }}>
                    <TextField
                      fullWidth
                      required
                      disabled={isSubmitting}
                      label="Plan name"
                      placeholder="Eg.12-Week Metabolism Reset"
                      size="small"
                      sx={CREATE_PLAN_TEXT_FIELD_SX}
                      value={programmeDraft.name}
                      onChange={(event) => updateCreateProgrammeForm(index, { name: event.target.value })}
                    />
                    <TextField
                      fullWidth
                      disabled={isSubmitting}
                      label="Description"
                      placeholder="What does this appointment involve ?"
                      size="small"
                      sx={CREATE_PLAN_TEXT_FIELD_SX}
                      value={programmeDraft.description}
                      onChange={(event) => updateCreateProgrammeForm(index, { description: event.target.value })}
                    />

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: 1.5 }}>
                      <Box sx={{ display: 'grid', gap: 0.75 }}>
                        <FieldLabel className="text-[0.9rem] font-semibold text-[#4B5563]">Type</FieldLabel>
                        <Select
                          value={programmeDraft.type}
                          onValueChange={(value) => {
                            setCreateProgrammeForms((prev) =>
                              prev.map((form, formIndex) =>
                                formIndex === index ? applyProgrammeTypeChange(form, value) : form,
                              ),
                            );
                          }}
                          disabled={isSubmitting}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PROGRAMME_TYPE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Box>
                      <Box sx={{ display: 'grid', gap: 0.75 }}>
                        <FieldLabel className="text-[0.9rem] font-semibold text-[#4B5563]" tooltip={PRICING_TYPE_HELPER}>
                          Pricing type
                        </FieldLabel>
                        <PricingTypeSelect
                          disabled={isSubmitting}
                          value={programmeDraft.pricingType}
                          onChange={(pricingType) => {
                            setCreateProgrammeForms((prev) =>
                              prev.map((form, formIndex) =>
                                formIndex === index ? applyPricingTypeChange(form, pricingType) : form,
                              ),
                            );
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: 1.5 }}>
                      <Box sx={{ display: 'grid', gap: 0.75 }}>
                        <FieldLabel className="text-[0.9rem] font-semibold text-[#4B5563]">Sessions included</FieldLabel>
                        <Input
                          type="number"
                          required
                          disabled={isSubmitting}
                          value={programmeDraft.numberOfSessions}
                          onChange={(event) => updateCreateProgrammeForm(index, { numberOfSessions: event.target.value })}
                        />
                      </Box>
                      {programmeDraft.pricingType === 'total_programme_price' ? (
                        <Box sx={{ display: 'grid', gap: 0.75 }}>
                          <FieldLabel className="text-[0.9rem] font-semibold text-[#4B5563]" tooltip={PROGRAMME_PRICING_HELPERS.total_programme_price}>
                            {totalProgrammePriceLabel}
                          </FieldLabel>
                          <Input
                            type="number"
                            disabled={isSubmitting}
                            placeholder="Enter Amount"
                            value={programmeDraft.rate}
                            onChange={(event) => updateCreateProgrammeForm(index, { rate: event.target.value })}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'grid', gap: 0.75 }}>
                          <FieldLabel className="text-[0.9rem] font-semibold text-[#4B5563]">Duration per session</FieldLabel>
                          <Select
                            value={programmeDraft.sessionDurationMinutes}
                            onValueChange={(value) => updateCreateProgrammeForm(index, { sessionDurationMinutes: value })}
                            disabled={isSubmitting}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="30">30 mins</SelectItem>
                              <SelectItem value="45">45 mins</SelectItem>
                              <SelectItem value="60">60 mins</SelectItem>
                            </SelectContent>
                          </Select>
                        </Box>
                      )}
                    </Box>

                    {programmeDraft.pricingType === 'price_per_session' ? (
                      <Box sx={{ display: 'grid', gap: 0.75 }}>
                        <FieldLabel className="text-[0.9rem] font-semibold text-[#4B5563]" tooltip={PROGRAMME_PRICING_HELPERS.price_per_session}>
                          {amountPerSessionLabel}
                        </FieldLabel>
                        <Input
                          type="number"
                          disabled={isSubmitting}
                          placeholder="Enter Amount"
                          value={programmeDraft.amountPerSession}
                          onChange={(event) => updateCreateProgrammeForm(index, { amountPerSession: event.target.value })}
                        />
                        <PerSessionTotalSummary form={programmeDraft} currencySymbol={currencySymbol} />
                      </Box>
                    ) : null}

                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.8125rem', color: '#0A0A0A', fontWeight: 500 }}>
                        Make it Active
                      </Typography>
                      <Switch
                        checked={programmeDraft.isActive}
                        disabled={isSubmitting}
                        onCheckedChange={(checked) => updateCreateProgrammeForm(index, { isActive: checked })}
                      />
                    </Box>

                  </Box>
                ))}
                <button
                  type="button"
                  onClick={() => setCreateProgrammeForms((prev) => [...prev, { ...EMPTY_PROGRAMME_FORM }])}
                  className="w-fit text-left text-[0.8125rem] font-bold text-[#3F9FEE]"
                >
                  Add More +
                </button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: createPlanStep === 2 ? 'space-between' : 'flex-end', borderTop: '1px solid #F3F4F6', bgcolor: '#fff', flexShrink: 0, px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
            {createPlanStep === 2 ? (
              <MuiButton size="small" disabled={isSubmitting} onClick={() => setCreatePlanStep(1)} sx={{ color: 'rgba(0,0,0,0.6)', textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600 }}>
                Back
              </MuiButton>
            ) : null}
            <MuiButton
              variant="contained"
              size="small"
              disabled={isSubmitting}
              onClick={() => void handleCreatePlanAction()}
              sx={{
                minWidth: 88,
                borderRadius: '10px',
                bgcolor: '#20AEDC',
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1899C3', boxShadow: 'none' },
              }}
            >
              {isSubmitting ? 'Saving...' : createPlanStep === 1 ? 'Next' : 'Save plan'}
            </MuiButton>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditPlanOpen} onOpenChange={(open) => !isSubmitting && setIsEditPlanOpen(open)}>
        <DialogContent aria-describedby={undefined} className="!flex max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[480px] flex-col gap-0 overflow-hidden rounded-xl border border-[#F3F4F6] bg-white p-0 sm:max-h-[calc(100dvh-3rem)] sm:w-[92vw] [&>button]:hidden">
          <DialogTitle className="sr-only">Edit Package</DialogTitle>
          <DialogDescription className="sr-only">Edit package name and description.</DialogDescription>
          <div className="min-h-0 flex-1 overflow-y-auto border-b border-[#F3F4F6] p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold leading-tight text-[#101828] sm:text-xl">Edit Package</h3>
                <p className="mt-2 text-xs text-[#6A7282] sm:text-sm">Update your package name and description here</p>
              </div>
              <button
                type="button"
                aria-label="Close edit package"
                disabled={isSubmitting}
                onClick={() => setIsEditPlanOpen(false)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6] text-[#6B7280] hover:text-[#4B5563] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {dialogFeedback ? (
              <div className="mt-4">
                <InlineFeedback
                  variant={dialogFeedback.variant}
                  title={dialogFeedback.title}
                  message={dialogFeedback.message}
                />
              </div>
            ) : null}

            <div className="mt-4 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-plan-name" className="text-xs">Package Name *</Label>
                <Input
                  id="edit-plan-name"
                  required
                  disabled={isSubmitting}
                  value={editPlanForm.name}
                  onChange={(event) => setEditPlanForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-plan-description" className="text-xs">Description</Label>
                <textarea
                  id="edit-plan-description"
                  disabled={isSubmitting}
                  value={editPlanForm.description}
                  onChange={(event) => setEditPlanForm((prev) => ({ ...prev, description: event.target.value }))}
                  className="min-h-[88px] w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
            </div>
          </div>

          <div className="flex shrink-0 justify-end border-t border-[#F3F4F6] px-4 py-3 sm:px-5">
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={() => void handleSavePlan()}
              className="h-9 w-full rounded-lg bg-[#20AEDC] px-4 text-xs font-semibold text-white hover:bg-[#1899C3] sm:w-auto sm:text-sm"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <RemoveDialog
        open={isRemovePlanOpen}
        title="Remove this Package?"
        message={`Are you sure you want to delete ${selectedPlanName || 'this package'}? Deleting this package will also remove all plans under it.`}
        isSubmitting={isSubmitting}
        feedback={isRemovePlanOpen ? dialogFeedback : null}
        onCancel={() => setIsRemovePlanOpen(false)}
        onConfirm={() => void handleConfirmRemovePlan()}
      />
      <RemoveDialog
        open={isRemoveProgrammeOpen}
        title="Remove this Plan?"
        message={`Are you sure you want to delete ${selectedProgrammeTitle || 'this plan'}?`}
        isSubmitting={isSubmitting}
        feedback={isRemoveProgrammeOpen ? dialogFeedback : null}
        onCancel={() => setIsRemoveProgrammeOpen(false)}
        onConfirm={() => void handleConfirmRemoveProgramme()}
      />
      <RemoveDialog
        open={isRemoveConsultationTypeOpen}
        title="Remove this Consultation Type?"
        message={`Are you sure you want to delete ${selectedConsultationTypeTitle || 'this consultation type'}?`}
        isSubmitting={isSubmitting}
        feedback={isRemoveConsultationTypeOpen ? dialogFeedback : null}
        onCancel={() => setIsRemoveConsultationTypeOpen(false)}
        onConfirm={() => void handleConfirmRemoveConsultationType()}
      />
    </>
  );
};

export default PlansAndBillingView;
